import { useEffect, useState, useMemo } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { isLogin } from "@utils/Session";
import { resolveRouteId } from "@/security/routes.catalog";
import { RoleApi } from "@/api/RoleApi";
import { getRoleId } from "@utils/Auth";
import { showAlert } from "@utils/Alert";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { setUserRole } from "@/store/slices/userSlides";
import { SuscriptionDataType } from "@/api/SuscriptionsApi";
import { isFeatureAllowed } from "@/security/features.catalog";

type RoleRules = Record<string, boolean>;
const SAFE_PATHS = new Set<string>(["/user/dashboard", "/"]);
const TENANT_MANAGE_URL = "/user/tenant-manage";

const PrivateRoute = () => {
    const [isUserLoggedIn, setLoginState] = useState<boolean | undefined>(undefined);
    const [rules, setRules] = useState<RoleRules | null>(null);
    const [loading, setLoading] = useState(true);
    const location = useLocation();
    const dispatch = useAppDispatch()
    const userRoleData = useAppSelector(state => state.user.userRoleData)
    const suscriptionData = useAppSelector(state => state.suscription.suscription || {})
    const tenantData = useAppSelector(state => state.suscription.tenantData || {})
    const planFeatures = useAppSelector(state => state.suscription.planFeatures)

    useEffect(() => {
        isLogin().then((loggedIn) => setLoginState(loggedIn));
    }, []);

    useEffect(() => {
        if (!isUserLoggedIn || !suscriptionData?.id) return;
        let alive = true;
        (async () => {
            await getUserRules(alive)
        })();
        return () => {
            alive = false;
        };
    }, [isUserLoggedIn, suscriptionData?.id]);

    const getUserRules = async (alive: boolean) => {
        const roleId = await getRoleId();
        setLoading(true);
        try {
            if (roleId) {
                let rules
                if (userRoleData && userRoleData.id === roleId) {
                    rules = userRoleData.rules
                } else {
                    const role = await RoleApi.get(roleId);
                    rules = role.rules
                    dispatch(setUserRole(role))
                }
                const userRules = JSON.parse(rules)
                if (alive) setRules(userRules);
            }
        } catch (err) {
            console.error("[PrivateRoute] Error al cargar reglas:", err);
            if (alive) setRules({});
        } finally {
            if (alive) setLoading(false);
        }
    }

    const routeId = useMemo(() => resolveRouteId(location.pathname), [location.pathname]);

    // Gating por plan de suscripción: independiente del ACL de roles (disabled_acl no aplica).
    // planFeatures undefined (aún cargando) o null (plan sin restricciones) => permitir.
    // Se excluye la ruta de administración de suscripción para no crear un lock-out.
    const isPlanAllowed = useMemo(() => {
        if (!routeId || routeId === "profile:manage") return true;
        return isFeatureAllowed(planFeatures, routeId);
    }, [routeId, planFeatures]);

    const isAllowed = useMemo(() => {
        const { disabled_acl = false } = suscriptionData as SuscriptionDataType;
        if (disabled_acl) {
            return true
        }

        if (!routeId) return true; // si no está en catálogo, permitir
        return rules?.[routeId] ?? false;
    }, [routeId, rules]);

    if (isUserLoggedIn === false) {
        showAlert("danger", "Usted no tiene permiso para acceder a esta seccion")
        return <Navigate to="/auth/login" replace />;
    }

    if (isUserLoggedIn === undefined || loading) return null;
    if (!isUserLoggedIn) return <Navigate to="/auth/login" replace />;

    if (tenantData && tenantData?.id && tenantData.status == "paused" && tenantData.tipoPago == "onvo" && location.pathname != TENANT_MANAGE_URL) {
        showAlert("danger", "Antes de continuar, debes registrar una forma de pago válida para activar tu suscripción.")
        return <Navigate to={TENANT_MANAGE_URL} replace />;
    }

    const isSafePath = SAFE_PATHS.has(location.pathname.replace(/\/+$/, "") || "/");
    if (isSafePath) {
        return <Outlet />;
    }

    if (!isPlanAllowed) {
        showAlert("warning", "Tu plan actual no incluye esta función. Actualiza tu plan para usarla.")
        const target = routeId
            ? `${TENANT_MANAGE_URL}?feature=${encodeURIComponent(routeId)}`
            : TENANT_MANAGE_URL;
        return <Navigate to={target} replace />;
    }

    if (!isAllowed) {
        showAlert("danger", "Usted no tiene permiso para acceder a esta seccion")
        return <Navigate to="/user/dashboard" replace />;
    }

    return <Outlet />;
};

export default PrivateRoute;